import * as THREE from 'three';

// ── Portal definitions ────────────────────────────────────────────────────
export interface Portal {
  id: string;
  label: string;
  position: [number, number, number];
  color: number;
  colorHex: string;
  page: string;
}

const PORTALS: Portal[] = [
  { id: 'apps',     label: 'APPS',     position: [-5.5, 1.2, -3],  color: 0xeab308, colorHex: '#eab308', page: 'apps' },
  { id: 'projects', label: 'GITHUB',   position: [5.5,  1.2, -3],  color: 0x1a1a1a, colorHex: '#1a1a1a', page: 'projects' },
  { id: 'store',    label: 'STORE',    position: [0,    1.2, -7],   color: 0xe11d48, colorHex: '#e11d48', page: 'store' },
  { id: 'about',    label: 'ABOUT',    position: [-3.5, 1.2, 2],   color: 0xe11d48, colorHex: '#e11d48', page: 'about' },
  { id: 'blog',     label: 'DEVLOG',   position: [3.5,  1.2, 2],   color: 0xeab308, colorHex: '#eab308', page: 'blog' },
];

// ── Shaders ───────────────────────────────────────────────────────────────

const OCEAN_VERT = /* glsl */`
  uniform float uTime;
  uniform float uWaveHeight;
  uniform float uWaveFreq;
  uniform float uSmallWaveHeight;
  uniform float uSmallWaveFreq;

  varying vec2 vUv;
  varying float vElevation;

  float noise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float n = i.x + i.y * 57.0 + 113.0 * i.z;
    return mix(
      mix(mix(fract(sin(n)*43758.5453), fract(sin(n+1.0)*43758.5453), f.x),
          mix(fract(sin(n+57.0)*43758.5453), fract(sin(n+58.0)*43758.5453), f.x), f.y),
      mix(mix(fract(sin(n+113.0)*43758.5453), fract(sin(n+114.0)*43758.5453), f.x),
          mix(fract(sin(n+170.0)*43758.5453), fract(sin(n+171.0)*43758.5453), f.x), f.y),
      f.z
    );
  }

  void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);
    float e = sin(world.x * uWaveFreq + uTime * 0.8) *
              cos(world.z * uWaveFreq * 0.7 + uTime * 0.6) * uWaveHeight;
    e += noise(vec3(world.xz * uSmallWaveFreq, uTime * 0.4)) * uSmallWaveHeight;
    world.y += e;
    vElevation = e;
    vUv = uv;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const OCEAN_FRAG = /* glsl */`
  uniform vec3 uDeepColor;
  uniform vec3 uSurfaceColor;
  uniform float uFogDensity;
  uniform float uTime;

  varying vec2 vUv;
  varying float vElevation;

  void main() {
    float t = clamp((vElevation + 0.3) * 2.5, 0.0, 1.0);
    vec3 color = mix(uDeepColor, uSurfaceColor, t);
    // Sparkle at crests
    float sparkle = step(0.18, vElevation) * (sin(vUv.x * 80.0 + uTime * 3.0) * 0.5 + 0.5) * 0.3;
    color += vec3(0.0, sparkle, sparkle * 0.8);
    gl_FragColor = vec4(color, 0.92);
  }
`;

const PORTAL_FRAG = /* glsl */`
  uniform vec3 uColor;
  uniform float uTime;
  uniform float uOpacity;
  varying vec2 vUv;

  void main() {
    vec2 uv = vUv - 0.5;
    float r = length(uv);
    float ring = smoothstep(0.48, 0.45, r) * smoothstep(0.32, 0.38, r);
    float pulse = 0.7 + 0.3 * sin(uTime * 2.5);
    float glow = smoothstep(0.5, 0.0, r) * 0.15 * pulse;
    float alpha = (ring + glow) * uOpacity;
    gl_FragColor = vec4(uColor * (ring > 0.01 ? 1.5 : 0.8), alpha);
  }
`;

const PORTAL_VERT = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const STAR_VERT = /* glsl */`
  attribute float size;
  attribute float brightness;
  varying float vBrightness;
  uniform float uTime;
  void main() {
    vBrightness = brightness;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (220.0 / -mv.z) * (0.85 + 0.15 * sin(uTime * 2.0 + brightness * 6.28));
    gl_Position = projectionMatrix * mv;
  }
`;

const STAR_FRAG = /* glsl */`
  varying float vBrightness;
  uniform vec3 uColor;
  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    float alpha = smoothstep(0.5, 0.0, d) * vBrightness;
    gl_FragColor = vec4(uColor, alpha);
  }
`;

// ── Scene ─────────────────────────────────────────────────────────────────

export class DresbyScene {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private clock = new THREE.Clock();
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();

  private oceanMesh!: THREE.Mesh;
  private portalMeshes: THREE.Mesh[] = [];
  private portalGroups: THREE.Group[] = [];
  private starMesh!: THREE.Points;
  private particleMesh!: THREE.Points;
  private particlePositions!: Float32Array;
  private particleVelocities!: Float32Array;

  private targetCameraPos: THREE.Vector3 | null = null;
  private targetLookAt: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private currentLookAt: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private onNavigate: ((page: string) => void) | null = null;
  private pendingNav: string | null = null;

  private uniforms = {
    ocean: {} as Record<string, THREE.IUniform>,
    portals: [] as Array<Record<string, THREE.IUniform>>,
    stars: {} as Record<string, THREE.IUniform>,
    particles: {} as Record<string, THREE.IUniform>,
  };

  private isActive = true;
  private isMobile = window.innerWidth < 768;

  constructor(private canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: !this.isMobile,
      alpha: false,
    });
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      600
    );
    this.setup();
    this.build();
    this.bindEvents();
    this.animate();
  }

  private setup(): void {
    const dpr = Math.min(window.devicePixelRatio, this.isMobile ? 1.5 : 2);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;

    this.camera.position.set(0, 4.5, 10);
    this.camera.lookAt(0, 0, 0);

    this.scene.fog = new THREE.FogExp2(0xfafafa, 0.018);
    this.scene.background = new THREE.Color(0xfafafa);
  }

  private build(): void {
    this.addLights();
    this.addOcean();
    this.addPlatform();
    this.addPortals();
    this.addStarField();
    this.addParticles();
    this.addStructures();
  }

  private addLights(): void {
    this.scene.add(new THREE.AmbientLight(0xffffff, 5.0));

    const sun = new THREE.DirectionalLight(0xffffff, 1.2);
    sun.position.set(10, 30, 10);
    this.scene.add(sun);

    const fill = new THREE.DirectionalLight(0xe8f4ff, 0.5);
    fill.position.set(-10, 10, -10);
    this.scene.add(fill);

    const red = new THREE.PointLight(0xe11d48, 3, 20);
    red.position.set(0, 3, 0);
    this.scene.add(red);

    const gold = new THREE.PointLight(0xeab308, 2, 25);
    gold.position.set(-6, 2, -4);
    this.scene.add(gold);

    const schwarz = new THREE.PointLight(0x1a1a1a, 2, 20);
    schwarz.position.set(6, 2, -4);
    this.scene.add(schwarz);

    const amber = new THREE.PointLight(0xd97706, 1.5, 18);
    amber.position.set(0, 2, -8);
    this.scene.add(amber);
  }

  private addOcean(): void {
    const segs = this.isMobile ? 48 : 128;
    const geo = new THREE.PlaneGeometry(120, 120, segs, segs);

    this.uniforms.ocean = {
      uTime:           { value: 0 },
      uWaveHeight:     { value: 0.35 },
      uWaveFreq:       { value: 0.35 },
      uSmallWaveHeight:{ value: 0.12 },
      uSmallWaveFreq:  { value: 1.8 },
      uDeepColor:      { value: new THREE.Color(0x3a2a2a) },
      uSurfaceColor:   { value: new THREE.Color(0xe8d4c0) },
    };

    const mat = new THREE.ShaderMaterial({
      vertexShader:   OCEAN_VERT,
      fragmentShader: OCEAN_FRAG,
      uniforms: this.uniforms.ocean,
      transparent: true,
      side: THREE.DoubleSide,
    });

    this.oceanMesh = new THREE.Mesh(geo, mat);
    this.oceanMesh.rotation.x = -Math.PI / 2;
    this.oceanMesh.position.y = -2.8;
    this.scene.add(this.oceanMesh);
  }

  private addPlatform(): void {
    // Hex platform
    const shape = new THREE.Shape();
    const r = 3.5;
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2 - Math.PI / 6;
      const x = r * Math.cos(angle);
      const y = r * Math.sin(angle);
      if (i === 0) shape.moveTo(x, y); else shape.lineTo(x, y);
    }
    shape.closePath();

    const extrudeSettings = { depth: 0.25, bevelEnabled: false };
    const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const mat = new THREE.MeshStandardMaterial({
      color: 0xe8edf4,
      metalness: 0.3,
      roughness: 0.6,
      emissive: new THREE.Color(0xffffff),
      emissiveIntensity: 0.05,
    });

    const platform = new THREE.Mesh(geo, mat);
    platform.rotation.x = -Math.PI / 2;
    platform.position.y = -0.25;
    this.scene.add(platform);

    // Glowing edge outline
    const edges = new THREE.EdgesGeometry(geo);
    const edgeMat = new THREE.LineBasicMaterial({ color: 0xe11d48, linewidth: 1 });
    const wireframe = new THREE.LineSegments(edges, edgeMat);
    wireframe.rotation.x = -Math.PI / 2;
    wireframe.position.y = -0.24;
    this.scene.add(wireframe);

    // Grid on platform
    const gridHelper = new THREE.GridHelper(7, 14, 0xe11d48, 0xd0d8e8);
    gridHelper.position.y = 0.01;
    (gridHelper.material as THREE.Material).opacity = 0.3;
    (gridHelper.material as THREE.Material).transparent = true;
    this.scene.add(gridHelper);
  }

  private addPortals(): void {
    PORTALS.forEach((portal, idx) => {
      const group = new THREE.Group();
      group.position.set(...portal.position);
      group.userData = { portalPage: portal.page, portalId: portal.id };

      // Portal disc (ShaderMaterial)
      const portalUniforms: Record<string, THREE.IUniform> = {
        uColor:   { value: new THREE.Color(portal.color) },
        uTime:    { value: 0 },
        uOpacity: { value: 1.0 },
      };
      this.uniforms.portals.push(portalUniforms);

      const discGeo = new THREE.CircleGeometry(1.1, 64);
      const discMat = new THREE.ShaderMaterial({
        vertexShader: PORTAL_VERT,
        fragmentShader: PORTAL_FRAG,
        uniforms: portalUniforms,
        transparent: true,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const disc = new THREE.Mesh(discGeo, discMat);
      group.add(disc);

      // Ring torus
      const ringGeo = new THREE.TorusGeometry(1.1, 0.04, 16, 80);
      const ringMat = new THREE.MeshStandardMaterial({
        color: portal.color,
        emissive: new THREE.Color(portal.color),
        emissiveIntensity: 2.0,
        metalness: 0.8,
        roughness: 0.1,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      group.add(ring);

      // Inner ring accent
      const innerGeo = new THREE.TorusGeometry(0.9, 0.02, 8, 80);
      const innerMat = new THREE.MeshStandardMaterial({
        color: portal.color,
        emissive: new THREE.Color(portal.color),
        emissiveIntensity: 1.5,
        transparent: true,
        opacity: 0.6,
      });
      const innerRing = new THREE.Mesh(innerGeo, innerMat);
      group.add(innerRing);

      // Vertical light shaft
      const shaftGeo = new THREE.CylinderGeometry(0.02, 0.3, 4, 8);
      const shaftMat = new THREE.MeshBasicMaterial({
        color: portal.color,
        transparent: true,
        opacity: 0.08,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const shaft = new THREE.Mesh(shaftGeo, shaftMat);
      shaft.position.y = -2;
      group.add(shaft);

      // Point light at portal
      const light = new THREE.PointLight(portal.color, 3, 8);
      group.add(light);

      // Tiny bobbing offset per portal
      group.userData.bobOffset = idx * 0.8;
      group.userData.rotSpeed = 0.4 + idx * 0.08;

      this.portalMeshes.push(disc);
      this.portalGroups.push(group);
      this.scene.add(group);
    });
  }

  private addStarField(): void {
    const count = this.isMobile ? 800 : 2000;
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const brightness = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * 200;
      positions[i * 3 + 1] = Math.random() * 60 + 5;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 200;
      sizes[i]      = Math.random() * 2 + 0.5;
      brightness[i] = Math.random() * 0.8 + 0.2;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position',   new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('size',       new THREE.BufferAttribute(sizes, 1));
    geo.setAttribute('brightness', new THREE.BufferAttribute(brightness, 1));

    this.uniforms.stars = {
      uTime:  { value: 0 },
      uColor: { value: new THREE.Color(0x442222) },
    };

    const mat = new THREE.ShaderMaterial({
      vertexShader: STAR_VERT,
      fragmentShader: STAR_FRAG,
      uniforms: this.uniforms.stars,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.starMesh = new THREE.Points(geo, mat);
    this.scene.add(this.starMesh);
  }

  private addParticles(): void {
    const count = this.isMobile ? 300 : 800;
    this.particlePositions = new Float32Array(count * 3);
    this.particleVelocities = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      this.particlePositions[i * 3]     = (Math.random() - 0.5) * 20;
      this.particlePositions[i * 3 + 1] = Math.random() * 8 - 1;
      this.particlePositions[i * 3 + 2] = (Math.random() - 0.5) * 20;
      this.particleVelocities[i * 3]     = (Math.random() - 0.5) * 0.003;
      this.particleVelocities[i * 3 + 1] = Math.random() * 0.005 + 0.001;
      this.particleVelocities[i * 3 + 2] = (Math.random() - 0.5) * 0.003;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.particlePositions, 3));

    this.uniforms.particles = {
      uTime:  { value: 0 },
      uColor: { value: new THREE.Color(0xe11d48) },
    };

    const mat = new THREE.ShaderMaterial({
      vertexShader: STAR_VERT.replace('attribute float brightness;', 'attribute float brightness;').replace(
        'attribute float size;',
        'attribute float size;'
      ),
      fragmentShader: STAR_FRAG,
      uniforms: this.uniforms.particles,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    // Use a simpler point material for particles
    const pMat = new THREE.PointsMaterial({
      color: 0xe11d48,
      size: 0.06,
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.particleMesh = new THREE.Points(geo, pMat);
    this.scene.add(this.particleMesh);
  }

  private addStructures(): void {
    // Central holographic pillar
    const pillarGeo = new THREE.CylinderGeometry(0.08, 0.08, 4, 8);
    const pillarMat = new THREE.MeshStandardMaterial({
      color: 0xe11d48,
      emissive: new THREE.Color(0xe11d48),
      emissiveIntensity: 0.8,
      transparent: true,
      opacity: 0.7,
    });
    const pillar = new THREE.Mesh(pillarGeo, pillarMat);
    pillar.position.y = 2;
    this.scene.add(pillar);

    // Top orb on pillar
    const orbGeo = new THREE.SphereGeometry(0.25, 16, 16);
    const orbMat = new THREE.MeshStandardMaterial({
      color: 0xe11d48,
      emissive: new THREE.Color(0xe11d48),
      emissiveIntensity: 1.2,
    });
    const orb = new THREE.Mesh(orbGeo, orbMat);
    orb.position.y = 4.2;
    this.scene.add(orb);
    orb.userData.isOrb = true;

    // Floating small cubes (decorative)
    const cubePositions: [number, number, number][] = [
      [-2, 0.5, 1.5], [2, 0.8, 1.5], [-1.5, 1.2, -1], [1.5, 0.6, -1],
    ];
    cubePositions.forEach(([x, y, z], i) => {
      const geo = new THREE.BoxGeometry(0.2, 0.2, 0.2);
      const mat = new THREE.MeshStandardMaterial({
        color: [0xe11d48, 0xeab308, 0x1a1a1a, 0xd97706][i % 4],
        emissive: new THREE.Color([0xe11d48, 0xeab308, 0x1a1a1a, 0xd97706][i % 4]),
        emissiveIntensity: 1.5,
      });
      const cube = new THREE.Mesh(geo, mat);
      cube.position.set(x, y, z);
      cube.userData.floatSeed = i;
      this.scene.add(cube);
    });
  }

  // ── Animation loop ──────────────────────────────────────────────────────

  private animate = (): void => {
    if (!this.isActive) return;
    requestAnimationFrame(this.animate);

    const t = this.clock.getElapsedTime();

    // Update ocean
    this.uniforms.ocean.uTime.value = t;

    // Update portal shaders + rotation
    this.portalGroups.forEach((group, i) => {
      const u = this.uniforms.portals[i];
      u.uTime.value = t;
      group.rotation.y = t * group.userData.rotSpeed * 0.3;
      group.position.y = PORTALS[i].position[1] + Math.sin(t * 0.8 + group.userData.bobOffset) * 0.15;
    });

    // Update stars
    this.uniforms.stars.uTime.value = t;

    // Update particles (drift upward, reset when too high)
    const pos = this.particlePositions;
    const vel = this.particleVelocities;
    const count = pos.length / 3;
    for (let i = 0; i < count; i++) {
      pos[i * 3]     += vel[i * 3];
      pos[i * 3 + 1] += vel[i * 3 + 1];
      pos[i * 3 + 2] += vel[i * 3 + 2];
      if (pos[i * 3 + 1] > 10) {
        pos[i * 3 + 1] = -1;
        pos[i * 3]     = (Math.random() - 0.5) * 20;
        pos[i * 3 + 2] = (Math.random() - 0.5) * 20;
      }
    }
    (this.particleMesh.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;

    // Animate scene objects
    this.scene.traverse(obj => {
      if (obj.userData.floatSeed !== undefined) {
        obj.position.y += Math.sin(t * 1.2 + obj.userData.floatSeed * 2.1) * 0.001;
        obj.rotation.y += 0.005;
        obj.rotation.x += 0.003;
      }
      if (obj.userData.isOrb) {
        obj.position.y = 4.2 + Math.sin(t * 1.5) * 0.1;
        (obj as THREE.Mesh).scale.setScalar(1 + Math.sin(t * 2) * 0.05);
      }
    });

    // Camera smooth follow
    if (this.targetCameraPos) {
      this.camera.position.lerp(this.targetCameraPos, 0.04);
      this.currentLookAt.lerp(this.targetLookAt, 0.04);
      this.camera.lookAt(this.currentLookAt);
      if (this.camera.position.distanceTo(this.targetCameraPos) < 0.1) {
        this.targetCameraPos = null;
        if (this.pendingNav) {
          window.location.hash = this.pendingNav;
          this.pendingNav = null;
        }
      }
    } else {
      // Gentle idle orbit
      this.camera.position.x = Math.sin(t * 0.08) * 0.6;
      this.camera.position.y = 4.5 + Math.sin(t * 0.12) * 0.3;
      this.camera.lookAt(this.currentLookAt);
    }

    this.renderer.render(this.scene, this.camera);
  };

  // ── Interaction ──────────────────────────────────────────────────────────

  private bindEvents(): void {
    window.addEventListener('resize', this.onResize);
    this.canvas.addEventListener('click', this.onClick);
    this.canvas.addEventListener('touchend', this.onTouch);
    this.canvas.addEventListener('mousemove', this.onMouseMove);
  }

  private onResize = (): void => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  };

  private onMouseMove = (e: MouseEvent): void => {
    this.pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
    this.pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
    this.checkHover();
  };

  private onTouch = (e: TouchEvent): void => {
    const touch = e.changedTouches[0];
    this.pointer.x = (touch.clientX / window.innerWidth) * 2 - 1;
    this.pointer.y = -(touch.clientY / window.innerHeight) * 2 + 1;
    this.checkClick();
  };

  private onClick = (): void => {
    this.checkClick();
  };

  private checkHover(): void {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(this.portalMeshes, true);
    this.canvas.style.cursor = hits.length > 0 ? 'pointer' : 'default';
  }

  private checkClick(): void {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(
      this.portalGroups.flatMap(g => g.children),
      false
    );

    if (hits.length > 0) {
      const group = hits[0].object.parent as THREE.Group;
      const page = group.userData.portalPage as string;
      if (page && this.onNavigate) {
        this.flyToPortal(group.position, page);
      }
    }
  }

  private flyToPortal(target: THREE.Vector3, page: string): void {
    const dir = new THREE.Vector3().subVectors(
      this.camera.position,
      target
    ).normalize();
    this.targetCameraPos = new THREE.Vector3(
      target.x + dir.x * 4,
      target.y + 1,
      target.z + dir.z * 4
    );
    this.targetLookAt = target.clone();
    this.pendingNav = `#${page}`;
    this.onNavigate?.(page);
  }

  // ── Public API ───────────────────────────────────────────────────────────

  setNavigationHandler(fn: (page: string) => void): void {
    this.onNavigate = fn;
  }

  // Returns screen-space positions of portals for HTML label overlay
  getPortalScreenPositions(width: number, height: number): Array<{
    id: string; label: string; color: string; x: number; y: number; visible: boolean;
  }> {
    const results: ReturnType<typeof this.getPortalScreenPositions> = [];
    const tmp = new THREE.Vector3();

    PORTALS.forEach((portal, i) => {
      const group = this.portalGroups[i];
      tmp.copy(group.position);
      tmp.y += 1.5;
      tmp.project(this.camera);

      const x = (tmp.x * 0.5 + 0.5) * width;
      const y = (-tmp.y * 0.5 + 0.5) * height;
      const visible = tmp.z < 1;

      results.push({ id: portal.id, label: portal.label, color: portal.colorHex, x, y, visible });
    });

    return results;
  }

  resetCamera(): void {
    this.targetCameraPos = new THREE.Vector3(0, 4.5, 10);
    this.targetLookAt = new THREE.Vector3(0, 0, 0);
  }

  destroy(): void {
    this.isActive = false;
    window.removeEventListener('resize', this.onResize);
    this.canvas.removeEventListener('click', this.onClick);
    this.canvas.removeEventListener('touchend', this.onTouch);
    this.renderer.dispose();
  }
}

export { PORTALS };
